import React from 'react';
import {
  AlturaAsientoRosa,
  ProfundidadAsientoRosa,
  ReposabrazosRosa,
  RespaldoRosa,
  TelefonoRosa,
  PantallaRosa,
  RatonRosa,
  TecladoRosa,
} from '../../lib/rosa';

// ============================================================
// Diagramas de referencia ROSA — mismo lenguaje visual que
// src/components/rula/RulaBodyDiagrams.tsx y
// src/components/reba/RebaBodyDiagrams.tsx (viewBox 0 0 260 300,
// misma paleta oscura, mismo "pill" de etiqueta), pero con un enfoque
// de escena distinto: en vez de segmentos corporales articulados
// (RULA/REBA), estas son escenas de una persona SENTADA en un
// escritorio de oficina. La silueta (torso, cabeza, piernas, silla,
// escritorio) es propia de este archivo — no reutiliza las piezas de
// RulaBodyDiagrams — y se compone con líneas gruesas de extremo
// redondeado (strokeLinecap="round") en vez de rects rotados: mismo
// resultado visual (segmento "en pastilla"), cálculo más simple
// (extremos fijos, sin trigonometría de rotación).
//
// Piezas estáticas compartidas entre los 8 diagramas: GROUND_LINE,
// CHAIR_BACKREST_STATIC, CHAIR_SEAT_STATIC, CHAIR_LEGS_STATIC,
// THIGH_STATIC, SHIN_STATIC, UPPER_BODY_STATIC, DESK_STATIC. El tronco,
// el cuello y la cabeza salen siempre de SeatedUpperBody (ver
// "anatomía" más abajo), también cuando un diagrama los mueve. Los
// diagramas 1 (altura del asiento) y 4 (respaldo) redefinen localmente
// la silla/torso porque la propia geometría de la silla/torso es el
// dato que están ilustrando. Los diagramas 7
// (ratón) y 8 (teclado) usan escenas propias más pequeñas (vista
// superior y detalle de cerca, respectivamente) en vez de la silueta
// sentada completa — se explica el motivo en cada uno.
// ============================================================

const ACCENT = '#2FBF71';
const BASE = '#e2e8f0';
const MUTED = '#94a3b8';
const DARK = '#64748b';
const AMBER = '#f59e0b';
const BLUE = '#3b82f6';

function Pill({ x, y, w, h }: { x: number; y: number; w: number; h: number }) {
  return <rect x={x} y={y} width={w} height={h} rx={5} fill="#0b1220" opacity={0.9} stroke="#1e293b" strokeWidth={1} />;
}

const diagramSvgProps = { width: 260, height: 300, viewBox: '0 0 260 300' } as const;

// ===== Silueta sentada — anatomía =====
// De arriba hacia abajo: cabeza → cuello → línea de hombros → tronco →
// cadera apoyada en el asiento. El tronco es un "pastillón" con la
// parte SUPERIOR PLANA (esquinas redondeadas): ese borde es la línea de
// hombros (34 de ancho). Sobre su centro nace el cuello (14 de ancho,
// más angosto que la cabeza de 32; visible NECK_VISIBLE = 12, ~3/8 del
// alto de la cabeza) y encima la cabeza. La base del cuello queda fija
// sobre los hombros: las posturas de cuello (flexión/extensión) giran
// cuello y cabeza alrededor de esa base, la cabeza nunca se desplaza
// sola. Los brazos nacen de la articulación del hombro
// (shoulderJoint: SHOULDER_JOINT_DROP por debajo de la línea de
// hombros, sobre el eje del tronco), no del costado del tronco ni del
// cuello.
//
// Silueta base: cadera (98,195) — línea de hombros (98,128) —
// articulación del hombro (98,135) — cabeza (98,100) — rodilla
// (152,195) — pie (152,270). Silla y escritorio esquemáticos a la
// izquierda/derecha de la silueta, respectivamente.

type Pt = { x: number; y: number };

const TORSO_HALF_WIDTH = 17;
const SHOULDER_CORNER_R = 8;
const NECK_WIDTH = 14;
const NECK_VISIBLE = 12;
const HEAD_R = 16;
const SHOULDER_JOINT_DROP = 7;
// La base del cuello entra unos px en el tronco para que no quede
// ranura cuando el borde de hombros está inclinado (tronco reclinado).
const NECK_INSET = 4;

const HIP: Pt = { x: 98, y: 195 };
const SHOULDER_TOP: Pt = { x: 98, y: 128 };

const r1 = (n: number) => Math.round(n * 10) / 10;

function torsoAxis(hip: Pt, top: Pt) {
  const len = Math.hypot(top.x - hip.x, top.y - hip.y);
  const u = { x: (top.x - hip.x) / len, y: (top.y - hip.y) / len }; // cadera → hombros
  const n = { x: -u.y, y: u.x }; // perpendicular (ancho del tronco)
  return { u, n };
}

// Tronco: lados rectos, línea de hombros plana con esquinas redondeadas
// y base semicircular en la cadera (queda bajo el muslo/asiento).
function torsoPath(hip: Pt, top: Pt): string {
  const { u, n } = torsoAxis(hip, top);
  const h = TORSO_HALF_WIDTH;
  const r = SHOULDER_CORNER_R;
  const p = (b: Pt, nk: number, uk: number) => `${r1(b.x + n.x * nk + u.x * uk)},${r1(b.y + n.y * nk + u.y * uk)}`;
  return [
    `M ${p(hip, h, 0)}`,
    `L ${p(top, h, -r)}`,
    `Q ${p(top, h, 0)} ${p(top, h - r, 0)}`,
    `L ${p(top, -(h - r), 0)}`,
    `Q ${p(top, -h, 0)} ${p(top, -h, -r)}`,
    `L ${p(hip, -h, 0)}`,
    `A ${h} ${h} 0 0 0 ${p(hip, h, 0)}`,
    'Z',
  ].join(' ');
}

function neckDir(neckDeg: number): Pt {
  const rad = (neckDeg * Math.PI) / 180;
  return { x: Math.sin(rad), y: -Math.cos(rad) };
}

// Centro de la cabeza: base del cuello (centro de la línea de hombros)
// + (cuello visible + radio) en la dirección del cuello.
function headCenter(top: Pt, neckDeg: number): Pt {
  const d = neckDir(neckDeg);
  const k = NECK_VISIBLE + HEAD_R;
  return { x: r1(top.x + d.x * k), y: r1(top.y + d.y * k) };
}

function shoulderJoint(hip: Pt, top: Pt): Pt {
  const { u } = torsoAxis(hip, top);
  return { x: r1(top.x - u.x * SHOULDER_JOINT_DROP), y: r1(top.y - u.y * SHOULDER_JOINT_DROP) };
}

// neckDeg: 0 = cuello vertical; > 0 flexión (cabeza hacia adelante, hacia
// el escritorio); < 0 extensión (cabeza hacia atrás).
function SeatedUpperBody({
  hip,
  top,
  neckDeg = 0,
  torsoColor = BASE,
}: {
  hip: Pt;
  top: Pt;
  neckDeg?: number;
  torsoColor?: string;
}) {
  const d = neckDir(neckDeg);
  const head = headCenter(top, neckDeg);
  const neck = (
    <line
      x1={r1(top.x - d.x * NECK_INSET)} y1={r1(top.y - d.y * NECK_INSET)}
      x2={r1(top.x + d.x * (NECK_VISIBLE + NECK_INSET))} y2={r1(top.y + d.y * (NECK_VISIBLE + NECK_INSET))}
      stroke={BASE} strokeWidth={NECK_WIDTH}
    />
  );
  const headCircle = <circle cx={head.x} cy={head.y} r={HEAD_R} fill={BASE} />;
  // Mismo color: un solo grupo con opacidad, para que los solapes
  // cuello/tronco/cabeza no se vean más claros. Tronco resaltado
  // (opaco): el tronco tapa la base del cuello.
  if (torsoColor === BASE) {
    return (
      <g opacity={0.9}>
        {neck}
        <path d={torsoPath(hip, top)} fill={BASE} />
        {headCircle}
      </g>
    );
  }
  return (
    <>
      <g opacity={0.9}>{neck}</g>
      <path d={torsoPath(hip, top)} fill={torsoColor} />
      <g opacity={0.9}>{headCircle}</g>
    </>
  );
}

const HEAD_STATIC_CENTER = headCenter(SHOULDER_TOP, 0);
const SHOULDER_JOINT_STATIC = shoulderJoint(HIP, SHOULDER_TOP);

// ===== Silueta sentada — piezas estáticas compartidas =====

const GROUND_LINE = <line x1={20} y1={278} x2={244} y2={278} stroke={MUTED} strokeWidth={2} />;

const CHAIR_BACKREST_STATIC = (
  <line x1={76} y1={108} x2={76} y2={202} stroke={BASE} strokeWidth={14} strokeLinecap="round" opacity={0.85} />
);

const CHAIR_SEAT_STATIC = (
  <line x1={62} y1={201} x2={137} y2={201} stroke={BASE} strokeWidth={14} strokeLinecap="round" opacity={0.85} />
);

const CHAIR_LEGS_STATIC = (
  <>
    <line x1={66} y1={207} x2={66} y2={278} stroke={DARK} strokeWidth={4} />
    <line x1={133} y1={207} x2={133} y2={278} stroke={DARK} strokeWidth={4} />
  </>
);

const THIGH_STATIC = (
  <line x1={98} y1={195} x2={152} y2={195} stroke={BASE} strokeWidth={18} strokeLinecap="round" opacity={0.9} />
);

const SHIN_STATIC = (
  <>
    <line x1={152} y1={195} x2={152} y2={270} stroke={BASE} strokeWidth={16} strokeLinecap="round" opacity={0.9} />
    <ellipse cx={164} cy={274} rx={14} ry={5} fill={BASE} opacity={0.9} />
  </>
);

const UPPER_BODY_STATIC = <SeatedUpperBody hip={HIP} top={SHOULDER_TOP} />;

const DESK_STATIC = (
  <>
    <line x1={168} y1={152} x2={236} y2={152} stroke={BASE} strokeWidth={10} strokeLinecap="round" opacity={0.9} />
    <line x1={176} y1={157} x2={176} y2={278} stroke={DARK} strokeWidth={3} />
    <line x1={228} y1={157} x2={228} y2={278} stroke={DARK} strokeWidth={3} />
  </>
);

// ===== Detalle en vista frontal =====
// La inclinación lateral de la cabeza y el giro del cuello ocurren fuera
// del plano de la vista lateral (de perfil no se verían), así que se
// muestran en un recuadro en vista frontal. El busto frontal reutiliza
// SeatedUpperBody: el mismo tronco con línea de hombros plana, cuello y
// cabeza. En vista frontal, neckDeg pasa a ser la inclinación LATERAL
// (> 0 hacia la derecha del dibujo), y correr la línea de hombros
// respecto de la cadera inclina esa línea (un hombro más alto que el
// otro). Los brazos, la cara y el teléfono son propios del recuadro.

interface InsetBox { x: number; y: number; w: number; h: number; scale: number }

// Teléfono: arriba a la derecha, sobre el escritorio (el espacio libre
// más grande de esa escena). Termina en y = 104 para quedar por encima
// de la línea discontinua cabeza → teléfono del rango "alejado".
const INSET_TELEFONO: InsetBox = { x: 150, y: 30, w: 102, h: 74, scale: 0.65 };
// Pantalla: arriba a la izquierda (a la derecha está el monitor).
const INSET_PANTALLA: InsetBox = { x: 6, y: 32, w: 62, h: 74, scale: 0.55 };
const BUST_HEIGHT = 42; // cadera → línea de hombros del busto frontal

type FrontalDetalle = 'inclinacion_telefono' | 'giro_cuello';

function FrontalDetail({ detalle, clipId, box }: { detalle: FrontalDetalle; clipId: string; box: InsetBox }) {
  // Base del busto (cadera) dentro del recuadro.
  const insetHip: Pt = { x: box.x + box.w / 2, y: box.y + box.h - 4 };
  const inclinado = detalle === 'inclinacion_telefono';
  // Coordenadas de diseño a escala 1, cadera en (0,0).
  const hip: Pt = { x: 0, y: 0 };
  // Hombro derecho (del dibujo) más alto: se corre 5 px a la izquierda el
  // centro de la línea de hombros, que así queda inclinada.
  const top: Pt = { x: inclinado ? -5 : 0, y: -BUST_HEIGHT };
  const neckDeg = inclinado ? 28 : 0;
  const head = headCenter(top, neckDeg);
  const { u, n } = torsoAxis(hip, top);
  const arm = (side: 1 | -1) => {
    const c = { x: top.x + n.x * TORSO_HALF_WIDTH * side, y: top.y + n.y * TORSO_HALF_WIDTH * side };
    const s0 = { x: c.x + n.x * 4 * side - u.x * 5, y: c.y + n.y * 4 * side - u.y * 5 };
    return (
      <line x1={r1(s0.x)} y1={r1(s0.y)} x2={r1(s0.x - u.x * 34 + n.x * 3 * side)} y2={r1(s0.y - u.y * 34 + n.y * 3 * side)}
        stroke={BASE} strokeWidth={10} strokeLinecap="round" />
    );
  };
  const rad = (neckDeg * Math.PI) / 180;
  const up = { x: Math.sin(rad), y: -Math.cos(rad) }; // "arriba" de la cabeza
  const right = { x: Math.cos(rad), y: Math.sin(rad) }; // "derecha" de la cabeza
  const at = (dx: number, dy: number): Pt => ({
    x: r1(head.x + right.x * dx - up.x * dy),
    y: r1(head.y + right.y * dx - up.y * dy),
  });
  // Ojos centrados si la cara mira al frente (aunque la cabeza esté
  // inclinada); corridos hacia la derecha si la cabeza está girada.
  const eyes = inclinado ? [at(-6, -3), at(6, -3)] : [at(3, -3), at(11, -3)];
  // Teléfono entre la oreja derecha y el hombro derecho, girado con la cabeza.
  const phone = at(HEAD_R + 3, 12);
  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={6} />
        </clipPath>
      </defs>
      <rect x={box.x} y={box.y} width={box.w} height={box.h} rx={6} fill="#0b1220" opacity={0.9} stroke="#1e293b" strokeWidth={1} />
      <text x={box.x + box.w / 2} y={box.y + (box.w >= 90 ? 12 : 10)} fill={MUTED} fontSize={box.w >= 90 ? 10 : 8} fontWeight={600} textAnchor="middle">vista frontal</text>
      <g clipPath={`url(#${clipId})`}>
        <g transform={`translate(${insetHip.x} ${insetHip.y}) scale(${box.scale})`}>
          <g opacity={0.9}>{arm(1)}{arm(-1)}</g>
          <SeatedUpperBody hip={hip} top={top} neckDeg={neckDeg} />
          {eyes.map((e, i) => <circle key={i} cx={e.x} cy={e.y} r={2.2} fill={DARK} />)}
          {!inclinado && (
            // Nariz asomando por el lado hacia el que gira la cabeza.
            <path d={`M ${head.x + 14},${head.y - 4} L ${head.x + 21},${head.y + 3} L ${head.x + 14},${head.y + 5} Z`} fill={BASE} opacity={0.9} />
          )}
          {inclinado ? (
            <>
              <rect x={phone.x - 4.5} y={phone.y - 10} width={9} height={20} rx={2.5} fill={ACCENT}
                transform={`rotate(${neckDeg} ${phone.x} ${phone.y})`} />
              {/* Vertical de referencia desde la base del cuello: la cabeza se aparta de ella. */}
              <line x1={top.x} y1={top.y - 4} x2={top.x} y2={top.y - 38} stroke={AMBER} strokeWidth={2.5} strokeDasharray="5 4" />
            </>
          ) : (
            <>
              {/* Flecha de giro sobre la cabeza, hacia el lado al que mira. */}
              <path d={`M ${head.x - 16},${head.y - 24} Q ${head.x},${head.y - 34} ${head.x + 16},${head.y - 24}`} stroke={AMBER} strokeWidth={3} fill="none" strokeLinecap="round" />
              <path d={`M ${head.x + 9},${head.y - 31} L ${head.x + 17},${head.y - 24} L ${head.x + 8},${head.y - 21}`} stroke={AMBER} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </>
          )}
        </g>
      </g>
    </g>
  );
}

// ============================================================
// 1. Altura del asiento (A-1)
// ============================================================
// La silla completa (asiento, respaldo, piernas, torso, cabeza) se
// redibuja localmente porque su altura ES el dato ilustrado: la
// cadera sube/baja y el resto de la silueta (torso, cabeza, respaldo,
// patas de la silla) se desplaza en bloque con ella, mientras la
// rodilla permanece a una altura fija (referencia del suelo/mesa).

interface AlturaConfig {
  hipY: number;
  kneeY: number;
  footY: number;
  showThighGap: boolean;
  showFloorGap: boolean;
}

const ALTURA_CONFIG: Record<AlturaAsientoRosa, AlturaConfig> = {
  rodillas_90: { hipY: 195, kneeY: 195, footY: 270, showThighGap: false, showFloorGap: false },
  bajo_rodillas_menor_90: { hipY: 213, kneeY: 195, footY: 270, showThighGap: false, showFloorGap: false },
  alto_rodillas_mayor_90: { hipY: 177, kneeY: 195, footY: 270, showThighGap: true, showFloorGap: false },
  sin_contacto_suelo: { hipY: 165, kneeY: 195, footY: 248, showThighGap: false, showFloorGap: true },
};

const ALTURA_LABEL: Record<AlturaAsientoRosa, string> = {
  rodillas_90: 'rodilla a la altura de la cadera',
  bajo_rodillas_menor_90: 'silla baja — rodillas sobre la cadera',
  alto_rodillas_mayor_90: 'silla alta — rodillas bajo la cadera',
  sin_contacto_suelo: 'sin contacto con el suelo',
};

const ALTURA_LABEL_SIZE: Record<AlturaAsientoRosa, number> = {
  rodillas_90: 13,
  bajo_rodillas_menor_90: 11,
  alto_rodillas_mayor_90: 11,
  sin_contacto_suelo: 14,
};

const ALTURA_FOOT_LABEL: Record<AlturaAsientoRosa, string> = {
  rodillas_90: 'pie apoyado en el suelo',
  bajo_rodillas_menor_90: 'pie apoyado en el suelo',
  alto_rodillas_mayor_90: 'espacio sin apoyo bajo el muslo',
  sin_contacto_suelo: 'pie sin apoyo — brecha con el suelo',
};

export function RosaAlturaAsientoDiagram({ rango }: { rango: AlturaAsientoRosa }) {
  const { hipY, kneeY, footY, showThighGap, showFloorGap } = ALTURA_CONFIG[rango];
  // El respaldo conserva su altura original respecto de la cadera
  // (hipY - 89); el tronco usa el mismo alto que la silueta base.
  const backrestTopY = hipY - 89;
  const shoulderTop: Pt = { x: 98, y: hipY - (HIP.y - SHOULDER_TOP.y) };
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      <line x1={76} y1={backrestTopY} x2={76} y2={hipY + 6} stroke={BASE} strokeWidth={14} strokeLinecap="round" opacity={0.85} />
      <line x1={62} y1={hipY + 6} x2={137} y2={hipY + 6} stroke={BASE} strokeWidth={14} strokeLinecap="round" opacity={0.85} />
      <line x1={66} y1={hipY + 12} x2={66} y2={278} stroke={DARK} strokeWidth={4} />
      <line x1={133} y1={hipY + 12} x2={133} y2={278} stroke={DARK} strokeWidth={4} />
      {DESK_STATIC}
      <SeatedUpperBody hip={{ x: 98, y: hipY }} top={shoulderTop} />

      {/* pierna resaltada: la rodilla es el elemento evaluado en A-1 */}
      <line x1={98} y1={hipY} x2={152} y2={kneeY} stroke={ACCENT} strokeWidth={18} strokeLinecap="round" />
      <line x1={152} y1={kneeY} x2={152} y2={footY} stroke={ACCENT} strokeWidth={16} strokeLinecap="round" />
      <ellipse cx={164} cy={footY + 4} rx={14} ry={5} fill={ACCENT} />
      <circle cx={152} cy={kneeY} r={10} fill="none" stroke={ACCENT} strokeWidth={2} opacity={0.7} />

      {showThighGap && (
        <>
          <line x1={140} y1={kneeY + 10} x2={140} y2={hipY + 8} stroke={AMBER} strokeWidth={2} strokeDasharray="3 3" />
          <line x1={132} y1={kneeY + 10} x2={148} y2={kneeY + 10} stroke={AMBER} strokeWidth={2} />
        </>
      )}
      {showFloorGap && (
        <>
          <line x1={152} y1={footY + 8} x2={152} y2={278} stroke={AMBER} strokeWidth={2} strokeDasharray="3 3" />
          <path d={`M 147,${footY + 12} L 152,${footY + 8} L 157,${footY + 12}`} stroke={AMBER} strokeWidth={2} fill="none" />
          <path d="M 147,274 L 152,278 L 157,274" stroke={AMBER} strokeWidth={2} fill="none" />
        </>
      )}

      <Pill x={45} y={4} w={170} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={ALTURA_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{ALTURA_LABEL[rango]}</text>
      <Pill x={45} y={282} w={170} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">{ALTURA_FOOT_LABEL[rango]}</text>
    </svg>
  );
}

// ============================================================
// 2. Profundidad del asiento (A-2)
// ============================================================
// Silueta base sin cambios; solo varía el borde delantero del
// asiento (verde, es el elemento medido) y una flecha de medida entre
// ese borde y la parte posterior de la rodilla.

const SEAT_EDGE_X: Record<ProfundidadAsientoRosa, number> = {
  neutra_8cm: 138,
  menor_8cm: 150,
  mayor_8cm: 118,
};

const PROFUNDIDAD_LABEL: Record<ProfundidadAsientoRosa, string> = {
  neutra_8cm: 'espacio neutro (~8 cm)',
  menor_8cm: 'asiento demasiado profundo',
  mayor_8cm: 'asiento demasiado corto',
};

export function RosaProfundidadAsientoDiagram({ rango }: { rango: ProfundidadAsientoRosa }) {
  const edge = SEAT_EDGE_X[rango];
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      {CHAIR_BACKREST_STATIC}
      <line x1={62} y1={201} x2={edge} y2={201} stroke={ACCENT} strokeWidth={14} strokeLinecap="round" />
      {edge < 137 && (
        <line x1={edge} y1={201} x2={137} y2={201} stroke={BASE} strokeWidth={14} strokeLinecap="round" opacity={0.35} strokeDasharray="2 4" />
      )}
      {CHAIR_LEGS_STATIC}
      {DESK_STATIC}
      {THIGH_STATIC}
      {SHIN_STATIC}
      {UPPER_BODY_STATIC}

      <line x1={edge} y1={182} x2={152} y2={182} stroke={AMBER} strokeWidth={2} />
      <path d={`M ${edge + 4},178 L ${edge},182 L ${edge + 4},186`} stroke={AMBER} strokeWidth={2} fill="none" />
      <path d="M 148,178 L 152,182 L 148,186" stroke={AMBER} strokeWidth={2} fill="none" />

      {rango === 'mayor_8cm' && (
        <line x1={edge} y1={195} x2={152} y2={195} stroke={AMBER} strokeWidth={4} strokeDasharray="4 4" opacity={0.8} />
      )}
      {rango === 'menor_8cm' && <circle cx={152} cy={195} r={9} fill="none" stroke={AMBER} strokeWidth={2.5} />}

      <Pill x={45} y={4} w={170} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={13} fontWeight={700} textAnchor="middle">{PROFUNDIDAD_LABEL[rango]}</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">borde del asiento – hueco de la rodilla</text>
    </svg>
  );
}

// ============================================================
// 3. Reposabrazos (A-3)
// ============================================================
// Silueta base; el brazo (hombro→codo→mano) y el reposabrazos (si lo
// hay) se resaltan en verde. "sin_reposabrazos" simplemente no dibuja
// el reposabrazos y el brazo cuelga sin apoyo lateral.

interface ReposConfig {
  elbow: { x: number; y: number };
  hand: { x: number; y: number };
  armrest: { x1: number; y1: number; x2: number; y2: number } | null;
  warnUp: boolean;
}

const REPOSABRAZOS_CONFIG: Record<ReposabrazosRosa, ReposConfig> = {
  neutro_90_relajado: { elbow: { x: 120, y: 165 }, hand: { x: 144, y: 165 }, armrest: { x1: 104, y1: 165, x2: 144, y2: 165 }, warnUp: false },
  desviacion_alto_o_bajo: { elbow: { x: 114, y: 148 }, hand: { x: 140, y: 148 }, armrest: { x1: 100, y1: 148, x2: 140, y2: 148 }, warnUp: true },
  sin_reposabrazos: { elbow: { x: 108, y: 172 }, hand: { x: 106, y: 205 }, armrest: null, warnUp: false },
};

const REPOSABRAZOS_LABEL: Record<ReposabrazosRosa, string> = {
  neutro_90_relajado: 'codo a 90°, hombro relajado',
  desviacion_alto_o_bajo: 'reposabrazos alto o bajo',
  sin_reposabrazos: 'sin reposabrazos',
};

export function RosaReposabrazosDiagram({ rango }: { rango: ReposabrazosRosa }) {
  const { elbow, hand, armrest, warnUp } = REPOSABRAZOS_CONFIG[rango];
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      {CHAIR_BACKREST_STATIC}
      {CHAIR_SEAT_STATIC}
      {CHAIR_LEGS_STATIC}
      {DESK_STATIC}
      {THIGH_STATIC}
      {SHIN_STATIC}
      {UPPER_BODY_STATIC}

      {armrest && (
        <>
          <line x1={armrest.x1} y1={armrest.y1} x2={armrest.x2} y2={armrest.y2} stroke={ACCENT} strokeWidth={9} strokeLinecap="round" />
          <line x1={(armrest.x1 + armrest.x2) / 2} y1={armrest.y2} x2={(armrest.x1 + armrest.x2) / 2} y2={201} stroke={DARK} strokeWidth={3} />
        </>
      )}
      <line x1={SHOULDER_JOINT_STATIC.x} y1={SHOULDER_JOINT_STATIC.y} x2={elbow.x} y2={elbow.y} stroke={ACCENT} strokeWidth={13} strokeLinecap="round" />
      <line x1={elbow.x} y1={elbow.y} x2={hand.x} y2={hand.y} stroke={ACCENT} strokeWidth={11} strokeLinecap="round" />
      <circle cx={hand.x} cy={hand.y} r={7} fill={ACCENT} />

      {warnUp && (
        <path d="M 30,64 L 30,44 M 24,50 L 30,44 L 36,50" stroke={AMBER} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}

      <Pill x={45} y={4} w={170} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={13} fontWeight={700} textAnchor="middle">{REPOSABRAZOS_LABEL[rango]}</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">
        {armrest ? 'brazo apoyado en el reposabrazos' : 'brazo sin apoyo lateral'}
      </text>
    </svg>
  );
}

// ============================================================
// 4. Respaldo (A-4)
// ============================================================
// El respaldo (recto o con curva lumbar) y el torso (vertical,
// reclinado o inclinado hacia adelante) se redefinen localmente;
// piernas y escritorio reutilizan las piezas estáticas.

interface RespaldoConfig {
  shoulderX: number;
  bow: number;
  gap: boolean;
  straight: boolean;
}

const RESPALDO_CONFIG: Record<RespaldoRosa, RespaldoConfig> = {
  neutro_95_110: { shoulderX: 90, bow: 8, gap: false, straight: false },
  no_se_utiliza: { shoulderX: 114, bow: 0, gap: true, straight: true },
  sin_apoyo_lumbar_o_inadecuado: { shoulderX: 98, bow: 0, gap: false, straight: true },
  inclinacion_fuera_95_110: { shoulderX: 74, bow: 14, gap: false, straight: false },
};

const RESPALDO_LABEL: Record<RespaldoRosa, string> = {
  neutro_95_110: 'respaldo 95°–110°, apoyo lumbar',
  no_se_utiliza: 'respaldo no utilizado',
  sin_apoyo_lumbar_o_inadecuado: 'sin apoyo lumbar / inadecuado',
  inclinacion_fuera_95_110: 'inclinación fuera de 95°–110°',
};

const RESPALDO_LABEL_SIZE: Record<RespaldoRosa, number> = {
  neutro_95_110: 12,
  no_se_utiliza: 14,
  sin_apoyo_lumbar_o_inadecuado: 11,
  inclinacion_fuera_95_110: 11,
};

export function RosaRespaldoDiagram({ rango }: { rango: RespaldoRosa }) {
  const { shoulderX, bow, gap, straight } = RESPALDO_CONFIG[rango];
  const backrestColor = gap ? BASE : ACCENT;
  const backrestOpacity = gap ? 0.85 : 1;
  const backrestPath = straight ? 'M 76,108 L 76,202' : `M 76,108 Q ${76 + bow},155 76,202`;
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      <path d={backrestPath} stroke={backrestColor} strokeWidth={14} strokeLinecap="round" fill="none" opacity={backrestOpacity} />
      {CHAIR_SEAT_STATIC}
      {CHAIR_LEGS_STATIC}
      {DESK_STATIC}
      {THIGH_STATIC}
      {SHIN_STATIC}
      {/* El tronco se inclina con el respaldo; el cuello sigue vertical sobre la línea de hombros. */}
      <SeatedUpperBody hip={HIP} top={{ x: shoulderX, y: SHOULDER_TOP.y }} torsoColor={ACCENT} />

      {gap && <line x1={90} y1={165} x2={106} y2={165} stroke={AMBER} strokeWidth={2} strokeDasharray="3 3" />}

      <Pill x={40} y={4} w={180} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={RESPALDO_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{RESPALDO_LABEL[rango]}</text>
    </svg>
  );
}

// ============================================================
// 5. Teléfono (B-1)
// ============================================================
// Silueta base; el teléfono y (si aplica) el arco de "manos libres" o
// la línea de alcance se resaltan. "alejado_mayor_30cm" inclina el
// tronco/cabeza hacia el teléfono, apoyado sobre el escritorio.

interface TelefonoConfig {
  phoneX: number;
  // Teléfono junto a la oreja: se posiciona relativo a la cabeza
  // (phoneDy respecto de su centro); sobre el escritorio: phoneY fijo.
  phoneY: number | null;
  phoneDy: number;
  shoulderX: number;
  neckDeg: number;
  showHeadset: boolean;
  showReach: boolean;
}

const TELEFONO_CONFIG: Record<TelefonoRosa, TelefonoConfig> = {
  neutro_1mano_manosLibres: { phoneX: 118, phoneY: null, phoneDy: -12, shoulderX: 98, neckDeg: 0, showHeadset: true, showReach: false },
  alejado_mayor_30cm: { phoneX: 205, phoneY: 146, phoneDy: 0, shoulderX: 108, neckDeg: 10, showHeadset: false, showReach: true },
};

const TELEFONO_LABEL: Record<TelefonoRosa, string> = {
  neutro_1mano_manosLibres: 'teléfono cerca, cuello recto',
  alejado_mayor_30cm: 'teléfono alejado (>30 cm)',
};

export function RosaTelefonoDiagram({ rango, sujecionHombroCuello = false }: { rango: TelefonoRosa; sujecionHombroCuello?: boolean }) {
  const { phoneX, phoneY: fixedPhoneY, phoneDy, shoulderX, neckDeg, showHeadset, showReach } = TELEFONO_CONFIG[rango];
  const top: Pt = { x: shoulderX, y: SHOULDER_TOP.y };
  const head = headCenter(top, neckDeg);
  const joint = shoulderJoint(HIP, top);
  const phoneY = fixedPhoneY ?? head.y + phoneDy;
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      {CHAIR_BACKREST_STATIC}
      {CHAIR_SEAT_STATIC}
      {CHAIR_LEGS_STATIC}
      {DESK_STATIC}
      {THIGH_STATIC}
      {SHIN_STATIC}
      <SeatedUpperBody hip={HIP} top={top} neckDeg={neckDeg} />

      <rect x={phoneX - 6} y={phoneY - 9} width={12} height={20} rx={3} fill={ACCENT} />
      {showHeadset && (
        <path d={`M ${head.x + 14},${head.y - 4} A 14 14 0 0 1 ${head.x + 14},${head.y + 14}`} stroke={BLUE} strokeWidth={2.5} fill="none" strokeLinecap="round" />
      )}
      {showReach && (
        <>
          <line x1={joint.x} y1={joint.y} x2={phoneX - 2} y2={phoneY} stroke={ACCENT} strokeWidth={7} strokeLinecap="round" opacity={0.85} />
          <line x1={head.x} y1={head.y + 6} x2={phoneX} y2={phoneY - 14} stroke={AMBER} strokeWidth={2} strokeDasharray="3 3" />
        </>
      )}

      {sujecionHombroCuello && <FrontalDetail detalle="inclinacion_telefono" clipId="rosa-detalle-telefono" box={INSET_TELEFONO} />}

      <Pill x={38} y={4} w={184} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={13} fontWeight={700} textAnchor="middle">{TELEFONO_LABEL[rango]}</text>
      {sujecionHombroCuello && (
        <>
          <Pill x={28} y={282} w={204} h={16} />
          <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">teléfono sujeto entre hombro y oreja</text>
        </>
      )}
    </svg>
  );
}

// ============================================================
// 6. Pantalla (B-2)
// ============================================================
// Silueta base; el monitor (rectángulo verde) sube/baja respecto a la
// línea discontinua de altura de ojos, con un pequeño chevron que
// indica la flexión/extensión de cuello resultante.

interface PantallaConfig {
  top: number;
  bottom: number;
  chevron: 'none' | 'down' | 'up';
  neckDeg: number;
}

// La línea de ojos queda a la altura del centro de la cabeza neutra
// (HEAD_STATIC_CENTER.y = 100). Monitor neutro y alto conservan su
// distancia relativa a esa línea; el bajo no cambia (lo limita el
// escritorio). La flexión/extensión ocurre en el cuello (neckDeg).
const PANTALLA_CONFIG: Record<PantallaRosa, PantallaConfig> = {
  neutra_40_75cm_altura_ojos: { top: 84, bottom: 116, chevron: 'none', neckDeg: 0 },
  baja_menor_30grados: { top: 132, bottom: 150, chevron: 'down', neckDeg: 25 },
  alta_extension_cuello: { top: 46, bottom: 78, chevron: 'up', neckDeg: -20 },
};

const PANTALLA_LABEL: Record<PantallaRosa, string> = {
  neutra_40_75cm_altura_ojos: 'monitor a la altura de los ojos',
  baja_menor_30grados: 'monitor bajo — cuello flexionado',
  alta_extension_cuello: 'monitor alto — cuello en extensión',
};

const PANTALLA_LABEL_SIZE: Record<PantallaRosa, number> = {
  neutra_40_75cm_altura_ojos: 12,
  baja_menor_30grados: 11,
  alta_extension_cuello: 11,
};

export function RosaPantallaDiagram({ rango, giroCuello = false }: { rango: PantallaRosa; giroCuello?: boolean }) {
  const { top, bottom, chevron, neckDeg } = PANTALLA_CONFIG[rango];
  const eyeY = HEAD_STATIC_CENTER.y;
  return (
    <svg {...diagramSvgProps}>
      {GROUND_LINE}
      {CHAIR_BACKREST_STATIC}
      {CHAIR_SEAT_STATIC}
      {CHAIR_LEGS_STATIC}
      {DESK_STATIC}
      {THIGH_STATIC}
      {SHIN_STATIC}
      <SeatedUpperBody hip={HIP} top={SHOULDER_TOP} neckDeg={neckDeg} />

      <line x1={98} y1={eyeY} x2={224} y2={eyeY} stroke={AMBER} strokeWidth={1.5} strokeDasharray="4 4" opacity={0.8} />
      <rect x={180} y={top} width={44} height={bottom - top} rx={3} fill={ACCENT} />
      <line x1={202} y1={bottom} x2={202} y2={152} stroke={DARK} strokeWidth={4} />

      {/* Chevron delante de la cara (antes iba sobre el mentón, que ahora es el cuello). */}
      {chevron === 'down' && (
        <path d="M 130,116 L 138,124 L 146,116" stroke={AMBER} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}
      {chevron === 'up' && (
        <path d="M 130,88 L 138,80 L 146,88" stroke={AMBER} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      )}

      {giroCuello && <FrontalDetail detalle="giro_cuello" clipId="rosa-detalle-pantalla" box={INSET_PANTALLA} />}

      <Pill x={34} y={4} w={192} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={PANTALLA_LABEL_SIZE[rango]} fontWeight={700} textAnchor="middle">{PANTALLA_LABEL[rango]}</text>
      <Pill x={40} y={282} w={180} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">línea discontinua: altura de ojos</text>
    </svg>
  );
}

// ============================================================
// 7. Ratón (C-1)
// ============================================================
// Vista superior (planta), a diferencia de los demás diagramas ROSA
// (vista lateral) — necesaria para mostrar la alineación
// hombro-ratón, un desplazamiento lateral invisible de perfil. Usa su
// propia escena en miniatura (torso/cabeza vistos desde arriba,
// escritorio como rectángulo) en vez de la silueta sentada lateral.

interface RatonConfig {
  mouseX: number;
  mouseY: number;
  elbowX: number;
  elbowY: number;
  aligned: boolean;
}

const RATON_CONFIG: Record<RatonRosa, RatonConfig> = {
  neutro_alineado_hombro: { mouseX: 168, mouseY: 205, elbowX: 168, elbowY: 150, aligned: true },
  desviacion_no_alineado: { mouseX: 215, mouseY: 205, elbowX: 192, elbowY: 148, aligned: false },
};

const RATON_LABEL: Record<RatonRosa, string> = {
  neutro_alineado_hombro: 'ratón alineado con el hombro',
  desviacion_no_alineado: 'ratón desplazado — brazo en abducción',
};

export function RosaRatonDiagram({ rango }: { rango: RatonRosa }) {
  const { mouseX, mouseY, elbowX, elbowY, aligned } = RATON_CONFIG[rango];
  const shoulderX = 168;
  const shoulderY = 95;
  return (
    <svg {...diagramSvgProps}>
      <rect x={20} y={140} width={220} height={130} rx={8} fill={BASE} opacity={0.25} />
      <rect x={95} y={64} width={80} height={50} rx={22} fill={BASE} opacity={0.9} />
      {/* Vista superior: la cabeza queda ENCIMA de los hombros (centrada
          sobre el tronco), no detrás de ellos; el contorno oscuro la
          separa del tronco del mismo color. */}
      <circle cx={135} cy={88} r={15} fill={BASE} stroke="#0b1220" strokeWidth={3} />

      <line x1={shoulderX} y1={98} x2={shoulderX} y2={265} stroke={aligned ? ACCENT : AMBER} strokeWidth={1.5} strokeDasharray="4 4" opacity={0.7} />

      <line x1={shoulderX} y1={shoulderY} x2={elbowX} y2={elbowY} stroke={ACCENT} strokeWidth={13} strokeLinecap="round" />
      <line x1={elbowX} y1={elbowY} x2={mouseX} y2={mouseY} stroke={ACCENT} strokeWidth={11} strokeLinecap="round" />
      <rect x={mouseX - 10} y={mouseY - 16} width={20} height={30} rx={9} fill={ACCENT} />

      <Pill x={35} y={4} w={190} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={12} fontWeight={700} textAnchor="middle">{RATON_LABEL[rango]}</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">vista superior — línea: eje del hombro</text>
    </svg>
  );
}

// ============================================================
// 8. Teclado (C-2)
// ============================================================
// Vista lateral de cerca (zoom): antebrazo, muñeca y teclado. No
// reutiliza la silueta sentada completa porque a esta escala el
// torso/cabeza quedarían fuera de encuadre; mantiene la misma paleta
// y el mismo estilo de "pill" que el resto del archivo. La muñeca
// "recta" forma una línea antebrazo-mano casi continua; la
// "angulada" forma un quiebre visible en la muñeca.

interface TecladoConfig {
  wristX: number;
  wristY: number;
  angled: boolean;
}

const TECLADO_CONFIG: Record<TecladoRosa, TecladoConfig> = {
  neutro_muneca_recta: { wristX: 126, wristY: 152, angled: false },
  desviacion_extension_mayor_15: { wristX: 122, wristY: 132, angled: true },
};

const TECLADO_LABEL: Record<TecladoRosa, string> = {
  neutro_muneca_recta: 'muñeca recta, línea continua',
  desviacion_extension_mayor_15: 'muñeca en extensión >15°',
};

export function RosaTecladoDiagram({ rango }: { rango: TecladoRosa }) {
  const { wristX, wristY, angled } = TECLADO_CONFIG[rango];
  const elbow = { x: 30, y: 152 };
  const fingertip = { x: 172, y: 156 };
  return (
    <svg {...diagramSvgProps}>
      <line x1={20} y1={172} x2={240} y2={172} stroke={MUTED} strokeWidth={2} />
      <rect x={150} y={150} width={75} height={16} rx={3} fill={BASE} opacity={0.9} />

      <line x1={elbow.x} y1={elbow.y} x2={wristX} y2={wristY} stroke={ACCENT} strokeWidth={13} strokeLinecap="round" />
      <line x1={wristX} y1={wristY} x2={fingertip.x} y2={fingertip.y} stroke={ACCENT} strokeWidth={11} strokeLinecap="round" />
      <circle cx={wristX} cy={wristY} r={7} fill={ACCENT} />
      <circle cx={elbow.x} cy={elbow.y} r={9} fill={BASE} opacity={0.9} />

      {angled && (
        <path
          d={`M ${wristX - 14},${wristY + 4} A 16 16 0 0 1 ${wristX + 6},${wristY - 12}`}
          stroke={AMBER}
          strokeWidth={2.5}
          fill="none"
          strokeLinecap="round"
        />
      )}

      <Pill x={40} y={4} w={180} h={22} />
      <text x={130} y={20} fill="#e2e8f0" fontSize={13} fontWeight={700} textAnchor="middle">{TECLADO_LABEL[rango]}</text>
      <Pill x={55} y={282} w={150} h={16} />
      <text x={130} y={294} fill={MUTED} fontSize={11} fontWeight={600} textAnchor="middle">antebrazo – muñeca – teclado</text>
    </svg>
  );
}
